import useI18n from '@/shared/hooks/usei18n'
import { Button, GenericFullscreenDialog } from '@/common'
import ImportedGoodForm from '../components/importedGoodForm/ImportedGoodForm'
import {
  ImportedGoodFormData,
  validateImportedGoodForm
} from '../components/importedGoodForm/ImportedGoodForm.schema'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCreateImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import { toast } from 'react-hot-toast'
import gridEmissionFactorByCountry from '@/common/config/gridEmissionFactorByCountry'
import { getElectricityConsumption } from '@/shared/utils/getElectricityConsumption'
import { useGetDefaultValues } from '@/shared/hooks/goods'
import { useGetAllPartnerships } from '@/features/suppliers/hooks/partnerships'
import { useGetAllInstallationsForOrganization } from '@/features/suppliers/hooks/installations'
import { useGetAllSupplierEmissionsReportsForOrganization } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import { EMPTY_VALUE } from '../components/bulkUpdateSupplierEmissionsForm/BulkUpdateSupplierEmissionsForm.schema'

export default function ImportedGoodCreateDialog({
  isOpen,
  close,
  organizationId,
  cbamReportId,
  isRepresentative,
  reportingPeriodYear,
  periodId
}: {
  isOpen: boolean
  close: () => void
  organizationId: string
  cbamReportId: string
  isRepresentative: boolean
  reportingPeriodYear: number
  periodId: string
}) {
  const { t } = useI18n()

  const { mutateAsync: createImportedGood, isPending: isSaving } = useCreateImportedGood(
    organizationId,
    cbamReportId
  )

  const [formData, setFormData] = useState<ImportedGoodFormData>({
    importDate: '',
    description: '',
    cnCode: '',
    country: '',
    netMassInTonnes: 0,
    isUsingDefaultEmissions: true,
    previousCustomsProcedure: '',
    requestedCustomsProcedure: '',
    partnershipId: '',
    installationId: '',
    supplierEmissionsReportId: ''
  })

  // Fetch partnerships, installations, and emissions reports
  const { data: partnerships = [] } = useGetAllPartnerships(organizationId)
  const { data: installations = [] } = useGetAllInstallationsForOrganization(organizationId)
  const { data: emissionsReports = [] } =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)

  // Fetch goods codes for getAggregatedCategoryForCnCode
  const { data: goodsCodes } = useGoodsCodes({})

  const getAggregatedCategoryForCnCode = useMemo(() => {
    const byCnCode = new Map(
      (goodsCodes ?? []).map(goodsCode => [
        goodsCode.goodsCode,
        goodsCode.aggregatedGoodsCategory
      ])
    )
    return (cnCode: string) => byCnCode.get(cnCode)
  }, [goodsCodes])

  // Determine if using default emissions based on form data
  const isUsingDefaultEmissions = useMemo(() => {
    return !formData.supplierEmissionsReportId || formData.supplierEmissionsReportId === EMPTY_VALUE
  }, [formData.supplierEmissionsReportId])

  // Get selected emissions report
  const selectedEmissionsReport = useMemo(() => {
    if (!formData.supplierEmissionsReportId || formData.supplierEmissionsReportId === EMPTY_VALUE) {
      return undefined
    }
    return emissionsReports.find(report => report.id === formData.supplierEmissionsReportId)
  }, [formData.supplierEmissionsReportId, emissionsReports])

  // Get selected installation to determine country and goods family
  const selectedInstallation = useMemo(() => {
    if (!formData.installationId || formData.installationId === EMPTY_VALUE) {
      return undefined
    }
    return installations.find(inst => inst.id === formData.installationId)
  }, [formData.installationId, installations])

  // Update country when installation is selected
  useEffect(() => {
    if (selectedInstallation?.country && !formData.country) {
      setFormData(prev => ({ ...prev, country: selectedInstallation.country }))
    }
  }, [selectedInstallation, formData.country])

  // Update form data when emissions report is selected
  useEffect(() => {
    if (selectedEmissionsReport?.goods[0]) {
      setFormData(prev => ({
        ...prev,
        cnCode: prev.cnCode || selectedEmissionsReport.goods[0].cnCode || '',
        description: prev.description || selectedEmissionsReport.goods[0].goodsDescription || ''
      }))
    }
  }, [selectedEmissionsReport])

  const defaultValueQuery = useGetDefaultValues(
    formData.cnCode || '',
    formData.country || '',
    reportingPeriodYear
  )

  const defaultValues = defaultValueQuery.data ?? null

  const defaultSpecificDirectEmbeddedEmissions = useMemo(() => {
    return defaultValues?.directEmissions ? Number(defaultValues.directEmissions) : 0
  }, [defaultValues])

  const defaultSpecificIndirectEmbeddedEmissions = useMemo(() => {
    return defaultValues?.indirectEmissions ? Number(defaultValues.indirectEmissions) : 0
  }, [defaultValues])

  const defaultElectricityEmissionFactor = useMemo(() => {
    return (
      gridEmissionFactorByCountry[formData.country as keyof typeof gridEmissionFactorByCountry] || 0
    )
  }, [formData.country])

  const validationResult = useCallback(() => {
    return validateImportedGoodForm(formData)
  }, [formData])

  const isSaveDisabled = useMemo(() => {
    return isSaving || !validationResult().success
  }, [isSaving, validationResult])

  const onClose = useCallback(() => {
    setFormData({
      description: '',
      cnCode: '',
      country: '',
      customId: '',
      importDate: '',
      netMassInTonnes: 0,
      isUsingDefaultEmissions: true,
      previousCustomsProcedure: '',
      requestedCustomsProcedure: '',
      partnershipId: '',
      installationId: '',
      supplierEmissionsReportId: ''
    })
    close()
  }, [close])

  const onSave = useCallback(async () => {
    try {
      const validation = validationResult()
      if (!validation.success) {
        return
      }

      const isUsingDefaultEmissions = validation.data.isUsingDefaultEmissions

      const defaultElectricityConsumptionMWh = getElectricityConsumption(
        Number(defaultSpecificDirectEmbeddedEmissions),
        Number(defaultElectricityEmissionFactor)
      )

      const installationId =
        validation.data.installationId && validation.data.installationId !== EMPTY_VALUE
          ? validation.data.installationId
          : undefined
      const partnershipId =
        validation.data.partnershipId && validation.data.partnershipId !== EMPTY_VALUE
          ? validation.data.partnershipId
          : undefined
      const supplierEmissionsReportId =
        validation.data.supplierEmissionsReportId &&
        validation.data.supplierEmissionsReportId !== EMPTY_VALUE
          ? validation.data.supplierEmissionsReportId
          : undefined

      await createImportedGood({
        ...validation.data,
        isUsingDefaultEmissions: isUsingDefaultEmissions,
        installationId,
        supplierEmissionsReportId,
        partnershipId,
        defaultSpecificDirectEmbeddedEmissions,
        defaultSpecificIndirectEmbeddedEmissions,
        defaultElectricityConsumptionMWh:
          defaultElectricityConsumptionMWh && Number.isFinite(defaultElectricityConsumptionMWh)
            ? defaultElectricityConsumptionMWh
            : undefined
      })
      toast.success(
        t(
          'cbamReporting:cbamReportDetails.importedGoodCreateDialog.notification.importedGoodCreatedSuccessfully'
        )
      )
      onClose()
    } catch (error) {
      console.error(error)
      toast.error(
        t(
          'cbamReporting:cbamReportDetails.importedGoodCreateDialog.notification.failedToCreateImportedGood'
        )
      )
    }
  }, [
    createImportedGood,
    validationResult,
    defaultElectricityEmissionFactor,
    onClose,
    t,
    defaultSpecificDirectEmbeddedEmissions,
    defaultSpecificIndirectEmbeddedEmissions
  ])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.actions.add')}
          </Button>
        </div>
      }
    >
      <ImportedGoodForm
        organizationId={organizationId}
        data={formData}
        onChange={setFormData}
        isUsingDefaultEmissions={isUsingDefaultEmissions}
        goodsFamily={selectedInstallation?.goodsFamily}
        isRepresentative={isRepresentative}
        getAggregatedCategoryForCnCode={getAggregatedCategoryForCnCode}
        partnerships={partnerships}
        installations={installations}
        emissionsReports={emissionsReports}
        hasSelectedInstallation={
          !!formData.installationId && formData.installationId !== EMPTY_VALUE
        }
        isEditMode={false}
        periodId={periodId}
        cbamReportYear={reportingPeriodYear}
      />
    </GenericFullscreenDialog>
  )
}
